import { computed, ref, watch, type Ref } from "vue";
import { api } from "../api/Api";

/**
 * Shared promo-application flow for the back-end sales documents that
 * support promotions: Sales Invoices, Sales Quotations, Sales Orders.
 *
 * What it gives you, in one composable:
 *
 *   1. `headerDiscount` — the CART_PCT_OFF / CART_AMOUNT_OFF amount, so the
 *      ItemSalesGrid can show the right Discount/Total.
 *
 *   2. `recomputeHeaderDiscount()` — call from your `setData()` after
 *      `setFormData(record)` so reloads correctly back the header amount
 *      out of `invoice.discount_a − Σ line.discount_a`.
 *
 *   3. A debounced live preview that fires on cart changes: hits the same
 *      `revenue/sales/pos/preview-promotion` endpoint, mutates each cart
 *      line's `discount_a / value_before_tax / tax_amount / net_total` in
 *      place, and updates `headerDiscount`.
 *
 *   4. A pre-save chooser flow: `previewAndCollectFreeOverrides()` walks
 *      the user through one `FreeItemChooserModal` per multi-item giveaway
 *      pool and returns the `ChosenLine[]` to send as `free_lines_override`
 *      on the save POST, or `null` if they cancelled.
 *
 *   5. The chooser modal handlers (`onChooserApply`, `onChooserClose`) and
 *      its reactive state (`activeChooserPool`, `collectedOverrides`) for
 *      the template binding.
 *
 * Usage:
 *   const promo = useSalesPromoFlow(formData);
 *   // In setData(): promo.recomputeHeaderDiscount();
 *   // In handleSubmit before the POST:
 *   //   const overrides = await promo.previewAndCollectFreeOverrides();
 *   //   if (overrides === null) return; // user cancelled
 *   //   (formData.value as any).free_lines_override = overrides;
 *   // Bind in template:
 *   //   <ItemSalesGrid :header-discount="promo.headerDiscount.value" .../>
 *   //   <FreeItemChooserModal v-if="promo.activeChooserPool.value" ... />
 */
export type PoolItem = {
  items_id: number;
  code: string;
  name: string;
  sales_uom: string;
  sales_uom_qty: number;
  on_hand: number;
};
export type FreePool = {
  promo_uid: string;
  promo_name: string;
  free_total_qty: number;
  free_uom_id: number | null;
  items: PoolItem[];
  expense_account_id: number | null;
};
export type ChosenLine = {
  promo_uid: string;
  items_id: number;
  qty: number;
};

export function useSalesPromoFlow(formData: Ref<any>) {
  const headerDiscount = ref(0);

  /**
   * After loading a saved document from the server, derive the header-
   * level discount from `invoice.discount_a − Σ line.discount_a`. This
   * works for line-only promos (returns 0), cart-only promos (returns
   * the full header amount), and mixed cases.
   */
  const recomputeHeaderDiscount = () => {
    const inv: any = formData.value;
    const totalDisc = Number(inv?.discount_a) || 0;
    const lineSum = (inv?.items ?? []).reduce(
      (s: number, it: any) => s + (Number(it.discount_a) || 0),
      0
    );
    headerDiscount.value = Math.max(0, totalDisc - lineSum);
  };

  /* ─── Chooser modal state ─── */
  const activeChooserPool = ref<FreePool | null>(null);
  const pendingPoolQueue = ref<FreePool[]>([]);
  const collectedOverrides = ref<ChosenLine[]>([]);
  let chooserResolver: ((overrides: ChosenLine[] | null) => void) | null = null;

  const advanceChooser = () => {
    if (pendingPoolQueue.value.length === 0) {
      activeChooserPool.value = null;
      chooserResolver?.(collectedOverrides.value);
      chooserResolver = null;
      return;
    }
    activeChooserPool.value = pendingPoolQueue.value.shift() ?? null;
  };

  const runChooserFlow = (pools: FreePool[]): Promise<ChosenLine[] | null> => {
    collectedOverrides.value = [];
    pendingPoolQueue.value = [...pools];
    return new Promise((resolve) => {
      chooserResolver = resolve;
      advanceChooser();
    });
  };

  const onChooserApply = (lines: ChosenLine[]) => {
    collectedOverrides.value.push(...lines);
    advanceChooser();
  };

  const onChooserClose = () => {
    activeChooserPool.value = null;
    pendingPoolQueue.value = [];
    chooserResolver?.(null);
    chooserResolver = null;
  };

  /**
   * Hit the preview endpoint and, if any qualifying free-line promo has
   * a multi-item giveaway pool, walk the user through the chooser. Returns
   * the picked `ChosenLine[]` (possibly empty if no choosing was needed),
   * or `null` if the user cancelled — in which case the caller MUST abort
   * the save.
   */
  const previewAndCollectFreeOverrides = async (): Promise<
    ChosenLine[] | null
  > => {
    const items = (formData.value?.items ?? [])
      .filter((e: any) => e.items_id && Number(e.quantity) > 0)
      .map((it: any) => ({
        items_id: it.items_id,
        quantity: it.quantity,
        price: it.price,
        tax_rate: it.tax_rate ?? 0,
        sales_uom_qty: it.sales_uom_qty ?? 1,
      }));
    if (items.length === 0) return [];

    let previewResp: any = null;
    try {
      previewResp = await api.post("revenue/sales/pos/preview-promotion", {
        outlets_id: formData.value?.outlets_id || null,
        sales_channels_id: formData.value?.sales_channels_id || null,
        customers_id: formData.value?.customers_id || null,
        warehouses_id: formData.value?.warehouses_id || null,
        items,
      });
    } catch (e) {
      console.warn("free-item preview failed; falling back to default picks", e);
      return [];
    }
    if (!previewResp?.success) return [];

    const freePools: FreePool[] = previewResp.data?.free_pools ?? [];
    const choosable = freePools.filter((p) => (p.items?.length ?? 0) > 1);
    if (choosable.length === 0) return [];

    return await runChooserFlow(choosable);
  };

  /* ─── Debounced live preview ───
   * Fires the same preview endpoint on cart changes and writes the per-
   * line numbers back so the displayed Discount/Total stay in sync with
   * whatever the server would calculate on save. The fingerprint tracks
   * ONLY the inputs the evaluator keys off (items_id, qty, price, packing)
   * so a successful preview can't re-trigger itself.
   */
  const cartFingerprint = computed(() => {
    const items = formData.value?.items ?? [];
    if (!items.length) return "";
    return items
      .map(
        (i: any) =>
          `${i.items_id ?? ""}:${i.quantity ?? 0}:${i.price ?? 0}:${i.sales_uom_qty ?? 1}`
      )
      .join("|");
  });

  const runLivePreview = async () => {
    const items = (formData.value?.items ?? [])
      .filter((e: any) => e.items_id && Number(e.quantity) > 0)
      .map((it: any) => ({
        items_id: it.items_id,
        quantity: it.quantity,
        price: it.price,
        tax_rate: it.tax_rate ?? 0,
        sales_uom_qty: it.sales_uom_qty ?? 1,
      }));
    if (!items.length) {
      headerDiscount.value = 0;
      return;
    }
    let resp: any = null;
    try {
      resp = await api.post("revenue/sales/pos/preview-promotion", {
        outlets_id: formData.value?.outlets_id || null,
        sales_channels_id: formData.value?.sales_channels_id || null,
        customers_id: formData.value?.customers_id || null,
        warehouses_id: formData.value?.warehouses_id || null,
        items,
      });
    } catch (e) {
      console.warn("live preview failed", e);
      return;
    }
    if (!resp?.success) return;
    const { lines, header_discount } = resp.data ?? {};
    if (Array.isArray(lines)) {
      for (const line of lines) {
        const item: any = formData.value?.items?.[line.index];
        if (!item) continue;
        // Display-only on the back-end form; the server re-evaluates on
        // save and writes any bonus_qty rows itself.
        item.discount_a = line.discount_a;
        item.value_before_tax = line.value_before_tax;
        item.tax_amount = line.tax_amount;
        item.net_total = line.net_total;
      }
    }
    headerDiscount.value = Number(header_discount ?? 0);
  };

  let livePreviewTimer: ReturnType<typeof setTimeout> | null = null;
  watch(cartFingerprint, (fp) => {
    if (!fp) {
      headerDiscount.value = 0;
      return;
    }
    if (livePreviewTimer) clearTimeout(livePreviewTimer);
    livePreviewTimer = setTimeout(() => {
      livePreviewTimer = null;
      runLivePreview();
    }, 300);
  });

  return {
    headerDiscount,
    recomputeHeaderDiscount,
    activeChooserPool,
    collectedOverrides,
    previewAndCollectFreeOverrides,
    onChooserApply,
    onChooserClose,
  };
}
